'use client';

import React, { memo, useState, useEffect } from 'react';
import Link from 'next/link';
import { Clock, TrendingUp, Shield, CheckCircle, Info } from 'lucide-react';
import { FaDiscord } from 'react-icons/fa6';
import { CoinIcon } from '@/app/components/SvgIcons';

// ─── CONFIG ──────────────────────────────────────────────────────────────────

const STAKE_URL = 'https://stake.com/?offer=sokoloco&c=c9bb838557';
const DISCORD_URL = 'https://discord.com/invite/sokoloco';

const START_DATE = '2026-06-11T00:00:00.000000Z';
const END_DATE   = '2026-07-19T23:59:59.000000Z';
const SUBMIT_DEADLINE = 'July 21, 2026';

// ─── PRIZES ──────────────────────────────────────────────────────────────────

const prizePlacements = [
    { place: 1, amount: 180, points: 1800 },
    { place: 2, amount: 100, points: 1000 },
    { place: 3, amount: 60, points: 600 },
    { place: 4, amount: 35, points: 350 },
    { place: 5, amount: 25, points: 250 },
]

const totalPrize = prizePlacements.reduce((s, p) => s + p.amount, 0);
const totalPoints = prizePlacements.reduce((s, p) => s + p.points, 0);

// ─── LEADERBOARD DATA (edit manually) ────────────────────────────────────────

interface SportParticipant {
    placement: number;
    username: string;
    wins: number;
    totalOdds: number;
}

const sportParticipants: SportParticipant[] = [
    { placement: 1, username: 'Fru********ste', wins: 76, totalOdds: 0 },
    { placement: 2, username: 'Che*******981', wins: 66, totalOdds: 0 }, // +5
    { placement: 3, username: 's***20', wins: 27, totalOdds: 0 },
    { placement: 4, username: 'mis********tva', wins: 14, totalOdds: 0 },
    { placement: 5, username: 'Zs****hh', wins: 13, totalOdds: 0 },
    { placement: 6, username: 'zm***s00', wins: 8, totalOdds: 0 },
    { placement: 7, username: 'Kuk****17', wins: 5, totalOdds: 0 },
    { placement: 8, username: 'Sn***as', wins: 1, totalOdds: 0 },
];

// ─── HOW TO PARTICIPATE STEPS ────────────────────────────────────────────────

const howToSteps = [
    { step: '1', title: 'Register on Stake', desc: 'Sign up using referral code SOKOLOCO' },
    { step: '2', title: 'Bet on World Cup', desc: 'Place $5+ bets on FIFA World Cup 2026 matches with odds ≥ 1.5' },
    { step: '3', title: 'Place Bets', desc: 'All qualifying bets count — win or lose' },
    { step: '4', title: 'Copy Bet Link', desc: 'Copy the bet link after each bet — Stake nickname + Bet ID must be visible' },
    { step: '5', title: 'Submit on Discord', desc: `Open a ticket with all your proofs by ${SUBMIT_DEADLINE}` },
];

// ─── SUB-COMPONENTS ──────────────────────────────────────────────────────────

const TimeDisplay = memo(({ value, label }: { value: number; label: string }) => (
    <div className="text-center">
        <div className="backdrop-blur border-b border-green-400/30 rounded-xl p-4 mb-2">
            <span className="text-xl md:text-3xl font-mono font-bold text-green-300">
                {String(value).padStart(2, '0')}
            </span>
            <span className="text-xs font-mono text-green-400"> {label}</span>
        </div>
    </div>
));
TimeDisplay.displayName = 'TimeDisplay';

const BonusPointsTooltip = memo(() => {
    const [visible, setVisible] = useState(false);
    return (
        <div className="relative inline-flex items-center">
            <button
                onMouseEnter={() => setVisible(true)}
                onMouseLeave={() => setVisible(false)}
                onFocus={() => setVisible(true)}
                onBlur={() => setVisible(false)}
                className="text-green-400/60 hover:text-green-300 transition-colors duration-150 cursor-help"
                aria-label="Bonus Kick Points info"
            >
                <Info size={13} />
            </button>
            {visible && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 w-56 pointer-events-none">
                    <div className="backdrop-blur-sm bg-gray-900/95 rounded-xl p-3 shadow-xl shadow-black/60">
                        <p className="text-xs font-mono text-gray-200 text-center">You will receive extra Kick Points once the leaderboard ends.</p>
                    </div>
                </div>
            )}
        </div>
    );
});
BonusPointsTooltip.displayName = 'BonusPointsTooltip';

const getPlacementColor = (place: number) => {
    if (place === 1) return 'from-yellow-400 via-yellow-500 to-amber-600';
    if (place === 2) return 'from-gray-300 via-gray-400 to-gray-500';
    if (place === 3) return 'from-orange-400 via-amber-600 to-orange-700';
    return 'from-green-400 via-emerald-500 to-green-600';
};

const SportPlayerRow = memo(({ participant, prize }: {
    participant: SportParticipant;
    prize: typeof prizePlacements[0] | undefined;
}) => (
    <div className="backdrop-blur border-b border-green-400/30 rounded-xl p-4 hover:border-green-400 transition-all group">
        <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-lg bg-gradient-to-br flex items-center justify-center flex-shrink-0 ${getPlacementColor(participant.placement)}`}>
                <span className="text-xl font-mono font-bold text-black">{participant.placement}</span>
            </div>

            <div className="w-12 h-12 rounded-full border-2 border-green-400/40 bg-gray-800 flex items-center justify-center flex-shrink-0">
                <span className="text-lg">⚽</span>
            </div>

            <div className="flex-1 min-w-0">
                <p className="font-mono font-bold text-green-300 truncate">{participant.username}</p>
                <p className="text-xs font-mono text-green-400/60">{participant.wins} bets</p>
            </div>

            {prize && (
                <div className="flex flex-col items-end gap-2">
                    <div className="text-right backdrop-blur bg-green-400/10 rounded-lg px-4 py-2 border border-green-400/30">
                        <p className="font-mono font-bold text-green-300">${prize.amount}</p>
                    </div>
                    <div className="flex items-center justify-end gap-1 text-green-400 font-mono font-semibold text-xs px-1">
                        <span>+{prize.points.toLocaleString()}</span>
                        <CoinIcon size={11} />
                    </div>
                </div>
            )}
        </div>
    </div>
));
SportPlayerRow.displayName = 'SportPlayerRow';

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function SportLeaderboardView() {
    const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    const [isEnded, setIsEnded] = useState(false);
    const [hasStarted, setHasStarted] = useState(false);

    useEffect(() => {
        const endTime   = new Date(END_DATE).getTime();
        const startTime = new Date(START_DATE).getTime();

        const tick = () => {
            const now = Date.now();
            setHasStarted(now >= startTime);
            const distance = endTime - now;
            if (distance <= 0) {
                setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
                setIsEnded(true);
                return false;
            }
            setIsEnded(false);
            setTimeLeft({
                days: Math.floor(distance / (1000 * 60 * 60 * 24)),
                hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
                minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
                seconds: Math.floor((distance % (1000 * 60)) / 1000),
            });
            return true;
        };

        tick();
        const timer = setInterval(() => { if (!tick()) clearInterval(timer); }, 1000);
        return () => clearInterval(timer);
    }, []);

    return (
        <div>
            {/* ── Hero banner ─────────────────────────────────────────────── */}
            <div className="text-center mb-12">
                <h2 className="text-2xl md:text-4xl font-mono font-bold tracking-widest mb-2">
                    <span className="bg-gradient-to-r from-green-400 via-emerald-300 to-green-500 bg-clip-text text-transparent">
                        SPORTS BETTING LEADERBOARD ON STAKE
                    </span>
                </h2>
                <p className="text-green-300 font-mono text-sm md:text-base">
                    <span className="inline-block animate-pulse">{'['}</span> June 11 – July 19, 2026 &nbsp;•&nbsp; Most qualifying bets wins <span className="inline-block animate-pulse">{']'}</span>
                </p>
            </div>

            {/* ── Countdown ───────────────────────────────────────────────── */}
            <div className="max-w-2xl mx-auto mb-12 p-6">
                <div className="flex items-center justify-center gap-2 mb-4">
                    <Clock className="w-6 h-6 text-green-400 animate-pulse" />
                    <h2 className="text-sm md:text-base font-mono font-bold text-green-400">
                        {!hasStarted ? 'STARTS IN' : isEnded ? 'ENDED' : 'TIME LEFT'}
                    </h2>
                </div>
                <div className="grid grid-cols-4 gap-4">
                    <TimeDisplay value={isEnded ? 0 : timeLeft.days}    label="DAYS" />
                    <TimeDisplay value={isEnded ? 0 : timeLeft.hours}   label="HOURS" />
                    <TimeDisplay value={isEnded ? 0 : timeLeft.minutes} label="MINS" />
                    <TimeDisplay value={isEnded ? 0 : timeLeft.seconds} label="SECS" />
                </div>
            </div>

            {/* ── Total prize pool ─────────────────────────────────────────── */}
            <div className="text-center mb-12">
                <div className="inline-block backdrop-blur border-b border-green-400/30 rounded-xl px-8 py-4">
                    <p className="text-sm font-mono text-green-400 mb-1">TOTAL PRIZE POOL</p>
                    <p className="text-2xl md:text-4xl font-mono font-bold text-green-300">${totalPrize}</p>
                    <div className="flex items-center justify-center gap-1.5 mt-2 text-green-400 font-mono font-semibold text-sm">
                        <span>+ {totalPoints.toLocaleString()}</span>
                        <CoinIcon size={13} />
                        <BonusPointsTooltip />
                    </div>
                </div>
            </div>

            {/* ── Leaderboard ─────────────────────────────────────────────── */}
            <div className="max-w-4xl mx-auto mb-12">
                <div className="space-y-3">
                    {sportParticipants.map(p => (
                        <SportPlayerRow
                            key={p.placement}
                            participant={p}
                            prize={prizePlacements.find(pr => pr.place === p.placement)}
                        />
                    ))}
                </div>
            </div>

            {/* ── Rules + How to ──────────────────────────────────────────── */}
            <div className="w-full backdrop-blur border-b border-green-400/30 rounded-2xl p-8 mb-12">
                <h2 className="text-2xl md:text-4xl font-mono font-bold text-center mb-8 text-green-400">
                    <span className="inline-block animate-pulse">{'['}</span> HOW TO PARTICIPATE <span className="inline-block animate-pulse">{']'}</span>
                </h2>

                <div className="space-y-8">
                    {/* Steps */}
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                        {howToSteps.map((item, idx) => (
                            <div key={idx} className="text-center group/step">
                                <div className="inline-flex items-center justify-center w-12 h-12 bg-green-500 rounded-full text-black text-xl font-bold mb-3 transition-transform duration-300 group-hover/step:scale-110 group-hover/step:rotate-12">
                                    {item.step}
                                </div>
                                <h4 className="text-lg font-mono font-bold text-green-400 mb-2">{item.title}</h4>
                                <p className="text-sm text-gray-300 font-mono">{item.desc}</p>
                            </div>
                        ))}
                    </div>

                    {/* Three columns */}
                    <div className="border-t border-green-400/20 pt-8 grid grid-cols-1 md:grid-cols-3 gap-8">

                        <div>
                            <h3 className="text-green-400 font-mono font-bold mb-3 flex items-center gap-2">
                                <TrendingUp className="w-4 h-4" /> BETTING RULES
                            </h3>
                            <ul className="space-y-2">
                                {[
                                    <span key="a">Bet on <span className="text-green-300 font-bold">Stake</span> under code <span className="text-green-300 font-bold">SOKOLOCO</span></span>,
                                    'Minimum bet: $5 per wager.',
                                    'Minimum odds: 1.5 per bet.',
                                    'Single, parlay and combo bets all qualify.',
                                    'Placing multiple bets on the same outcome counts as one.',
                                ].map((item, i) => (
                                    <li key={i} className="flex items-start gap-2 font-mono text-sm text-gray-300">
                                        <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div>
                            <h3 className="text-green-400 font-mono font-bold mb-3 flex items-center gap-2">
                                <Shield className="w-4 h-4" /> ELIGIBILITY
                            </h3>
                            <ul className="space-y-2">
                                {[
                                    'Only FIFA World Cup 2026 match bets qualify.',
                                    <span key="b">Only bets from <span className="text-green-300 font-bold">June 11 – July 19, 2026</span> count.</span>,
                                    <span key="c">Submit all proofs by <span className="text-green-300 font-bold">{SUBMIT_DEADLINE}</span>.</span>,
                                ].map((item, i) => (
                                    <li key={i} className="flex items-start gap-2 font-mono text-sm text-gray-300">
                                        <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div>
                            <h3 className="text-green-400 font-mono font-bold mb-3 flex items-center gap-2">
                                <Info className="w-4 h-4" /> PROOF REQUIRED
                            </h3>
                            <ul className="space-y-2">
                                {[
                                    'Copy the bet link after each bet.',
                                    'Stake nickname must be visible.',
                                    'Bet ID must be visible.',
                                    <span key="a">Send proofs via Discord ticket <span className="text-green-300 font-bold">anytime</span> — we will update the leaderboard accordingly.</span>,
                                    <span key="b">All proofs must be submitted by <span className="text-green-300 font-bold">{SUBMIT_DEADLINE}</span> at the latest.</span>,
                                ].map((item, i) => (
                                    <li key={i} className="flex items-start gap-2 font-mono text-sm text-gray-300">
                                        <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                    </div>

                    {/* Note */}
                    <div className="mt-4 pt-4 border-t border-green-400/20">
                        <div className="bg-green-500/10 rounded-xl p-6 text-center">
                            <p className="text-gray-300 font-mono text-sm">
                                Ranking is based on the number of qualifying bets. Abusers will be <span className="text-gray-400 font-bold">disqualified</span> and have their prize forfeited.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── CTAs ────────────────────────────────────────────────────── */}
            <div className="text-center mt-12">
                <div className="flex flex-wrap items-center justify-center gap-4 mb-4">
                    <Link
                        href={STAKE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 rounded-xl shadow-lg hover:shadow-emerald-500/30 hover:border-green-400 transition-all duration-200 px-8 py-4 font-mono font-bold text-gray-200 relative overflow-hidden"
                    >
                        <TrendingUp className="inline-block w-5 h-5 mr-2" />
                        BET ON STAKE NOW
                    </Link>
                    <Link
                        href={DISCORD_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 rounded-xl shadow-lg hover:shadow-emerald-500/30 hover:border-green-400 transition-all duration-200 px-8 py-4 font-mono font-bold text-gray-200 relative overflow-hidden"
                    >
                        <FaDiscord className="w-5 h-5" />
                        SUBMIT ON DISCORD
                    </Link>
                </div>
                <p className="text-green-300/40 font-mono text-xs">
                    Use referral code <span className="text-green-300 font-bold">SOKOLOCO</span> when registering on Stake
                </p>
            </div>
        </div>
    );
}